import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { useButtonStyles } from 'components/UI/V2/Button/Button.styles';
import React from 'react';
import PulseLoader from 'react-spinners/PulseLoader';

export type ButtonVariant = 'primary' | 'green' | 'black' | 'danger' | 'outlined' | 'text-green';

export type ButtonProps = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'className'> & {
    label: string | React.ReactNode;
    fullWidth?: boolean;
    size?: 'small' | 'medium' | 'large' | 'inline';
    noColorOnDisabled?: boolean;
    hideLabelOnMobile?: boolean;
    onlyIcon?: boolean;
    variant?: ButtonVariant;
    isLoading?: boolean;
    leftIcon?: IconProp;
    rightIcon?: IconProp;
    className?: string;
};

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
    const classes = useButtonStyles();

    const spinner = (
        <div className={classes.spinner}>
            <PulseLoader
                size={5}
                loading={true}
                color="#ffffff"
            />
        </div>
    );

    // extract props and define default values
    const {
        size = 'medium',
        variant = 'primary',
        fullWidth = false,
        label,
        leftIcon,
        rightIcon,
        isLoading,
        onlyIcon,
        noColorOnDisabled,
        hideLabelOnMobile,
        ...buttonProps
    } = props;

    const buttonLabel = React.useMemo(() => {
        let label = props.label;

        if (hideLabelOnMobile) {
            label = <span className={classes.onlyDesktop}>{label}</span>;
        }

        return label;
    }, [label, hideLabelOnMobile]);

    return (
        <button
            {...buttonProps}
            data-only-icon={onlyIcon}
            data-full-width={fullWidth}
            data-no-color-on-disabled={noColorOnDisabled}
            className={clsx(classes.root, props.className, {
                // Sizes
                [classes.small]: size === 'small',
                [classes.medium]: size === 'medium',
                [classes.large]: size === 'large',
                [classes.inline]: size === 'inline',
                // States
                [classes.loading]: isLoading,
                // Variants
                [classes.primary]: variant === 'primary',
                [classes.green]: variant === 'green',
                [classes.black]: variant === 'black',
                [classes.danger]: variant === 'danger',
                [classes.outlined]: variant === 'outlined',
                [classes.textGreen]: variant === 'text-green'
            })}
        >
            {leftIcon && <FontAwesomeIcon icon={leftIcon} />}

            {!onlyIcon && buttonLabel}

            {rightIcon && <FontAwesomeIcon icon={rightIcon} />}

            {isLoading && spinner}
        </button>
    );
});

Button.displayName = 'Button';

export default Button;
